import {AsyncPipe, NgIf} from '@angular/common';
import {Component, inject, OnInit, ViewChild} from '@angular/core';
import {FlexLayoutModule} from '@angular/flex-layout';
import {FormsModule} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MatOptionModule} from '@angular/material/core';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatPaginator, MatPaginatorModule} from '@angular/material/paginator';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatSelectChange, MatSelectModule} from '@angular/material/select';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {MatTableDataSource, MatTableModule} from '@angular/material/table';
import {PatternType, RateType, SpacePricingRateCategory} from "@features/leasing/lease-bookings/bookings.model";
import {LoadingService} from "../../../../../../../../nter-ui-kit/src/lib/framework/util/service/loading.service";
import {toIsoDateEndOfDay} from "../../../../../../../../nter-ui-kit/src/lib/framework/util/static/date-utils";
import {AppSharedModule} from "../../../../../core/modules/app-shared.module";
import {PanelType} from '../../../../../shared/components/info-panel/info-panel.component';
import {DateUtilsService} from "../../../../../shared/services/dateUtils.service";
import {PaginatorService} from "../../../../../shared/services/paginator.service";
import {UtilsService} from "../../../../../shared/services/utils.service";
import {LeasePricingPeriod, OccupancyType, PaymentMethod, Period, RentalPricingOption} from '../../../leasing.model';
import {
  convertToDateObject,
  endDateMaxValue,
  endDateMinValue,
  startDateMaxValue,
  startDateMinValue
} from "../../../leasing.periods.util";

@Component({
  selector: 'app-edit-pricing-period-dialog',
  templateUrl: './edit-pricing-period-dialog.component.html',
  imports: [
    MatIconModule,
    MatProgressSpinnerModule,
    AppSharedModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    FormsModule,
    MatDatepickerModule,
    MatSlideToggleModule,
    MatTableModule,
    MatPaginatorModule,
    NgIf,
    AsyncPipe,
    FlexLayoutModule,
    MatInputModule,
    MatButtonModule,
  ],
  standalone: true
})
export class EditPricingPeriodDialogComponent implements OnInit {
  readonly dialogRef = inject(MatDialogRef<EditPricingPeriodDialogComponent>);
  readonly data = inject<any>(MAT_DIALOG_DATA);

  @ViewChild('paymentMethodsPaginator') set paymentMethodsPaginator(paginator: MatPaginator) {
    if (paginator) paginator._intl.getRangeLabel = this.paginatorService.getRangeDisplayText;
    if (this.paymentMethodsDataSource) this.paymentMethodsDataSource.paginator = paginator;
  }

  editType: 'values' | 'dates' = 'values';

  loading$ = this.loader.loading$;
  allPeriods: LeasePricingPeriod[] = [];
  selectedPeriod: LeasePricingPeriod;
  leaseId: string;
  paymentMethods: PaymentMethod[] = [];
  paymentMethodsDataSource: MatTableDataSource<PaymentMethod> = new MatTableDataSource<PaymentMethod>([]);
  displayedColumns = ['collectionType', 'transactionFeeType', 'transactionFee'];

  fixedRateTypes = [
    {label: 'Fixed Rental per m² per month', code: RentalPricingOption.FIXED_PER_SQM_PER_MONTH},
    {label: 'Fixed Rental per month', code: RentalPricingOption.FIXED_PER_MONTH},
    {label: 'Fixed Rental for period', code: RentalPricingOption.FIXED_FOR_PERIOD},
  ]

  patternTypes = [
    {label: 'Monthly', code: PatternType.MONTHLY},
    {label: 'Weekly', code: PatternType.WEEKLY},
    {label: 'Weekly (Custom)', code: PatternType.WEEKLY_CUSTOM},
    {label: 'Daily', code: PatternType.DAILY},
    {label: 'Daily', code: PatternType.DAILY_CUSTOM},
    {label: 'Weekends', code: PatternType.WEEKENDS},
  ]

  basePeriod = new Period();

  constructor(public utils: UtilsService,
              public loader: LoadingService,
              public paginatorService: PaginatorService,
              public dateUtils: DateUtilsService) {
    this.editType = this.data.editType;

    this.allPeriods = this.data.allPeriods;
    this.allPeriods.forEach(period => {
      period.startDate = convertToDateObject(period.startDate);
      period.endDate = convertToDateObject(period.endDate);
      period.earliestTransactionDate = convertToDateObject(period.earliestTransactionDate);
      period.latestTransactionDate = convertToDateObject(period.latestTransactionDate);
    })

    this.selectedPeriod = this.data.selectedPeriod;
    this.selectedPeriod.startDate = convertToDateObject(this.selectedPeriod.startDate);
    this.selectedPeriod.endDate = convertToDateObject(this.selectedPeriod.endDate);
    this.selectedPeriod.earliestTransactionDate = convertToDateObject(this.selectedPeriod.earliestTransactionDate);
    this.selectedPeriod.latestTransactionDate = convertToDateObject(this.selectedPeriod.latestTransactionDate);

    // changing the end date will update the selected period object, so keep original for comparison
    this.basePeriod.startDate = this.selectedPeriod.startDate;
    if (this.selectedPeriod.endDate) this.basePeriod.endDate = this.selectedPeriod.endDate;

    this.setBookingPricing();

    this.leaseId = this.data.leaseId;
    this.paymentMethods = this.data.paymentMethods;
    this.paymentMethodsDataSource.data = this.data.paymentMethods;
  }

  ngOnInit(): void {
  }

  close() {
    this.dialogRef.close();
  }

  cancelChanges(): void {
    this.dialogRef.close({save: false, periodToSave: null})
  }

  setBookingPricing() {
    if (this.selectedPeriod.bookingPricing == null) {
      const bookingPricing = {
        patternType: PatternType.MONTHLY,
        includeDays: [],
        excludeDays: [],
        customRates: false,
        bookingRates: [],
        bookingEstimation: null,
        spacePricingRateCategory: SpacePricingRateCategory.PER_SQM,
        applyTransactionFees: false,
        initialBillablePeriods: []
      }
      bookingPricing.applyTransactionFees = this.selectedPeriod.applyTransactionFee ?? false;
      switch (this.selectedPeriod.rentalPricingOption) {
        case RentalPricingOption.FIXED_PER_SQM_PER_MONTH:
          bookingPricing.patternType = PatternType.MONTHLY;
          bookingPricing.spacePricingRateCategory = SpacePricingRateCategory.PER_SQM;
          break;
        case RentalPricingOption.FIXED_PER_MONTH:
          bookingPricing.patternType = PatternType.MONTHLY;
          bookingPricing.spacePricingRateCategory = SpacePricingRateCategory.PER_PERIOD;
          break;
        case RentalPricingOption.FIXED_FOR_PERIOD:
          bookingPricing.patternType = PatternType.DAILY_CUSTOM;
          bookingPricing.spacePricingRateCategory = SpacePricingRateCategory.PER_PERIOD;
          break;
        case RentalPricingOption.PRICING_MODEL:
          bookingPricing.patternType = PatternType.MONTHLY;
          bookingPricing.spacePricingRateCategory = SpacePricingRateCategory.PER_SQM;
          break;
        case RentalPricingOption.TURNOVER_PERCENTAGE:
          bookingPricing.patternType = PatternType.MONTHLY;
          bookingPricing.spacePricingRateCategory = SpacePricingRateCategory.PER_SQM;
          break;
      }
      this.selectedPeriod.bookingPricing = {...bookingPricing};
    }
  }

  onFixedRateTypeChange(event: MatSelectChange) {
    this.selectedPeriod.bookingPricing?.bookingRates
      .forEach(rate => {
        if (rate.rateType == RateType.FIXED) {
          rate.category = event.value;
        }
      });
  }

  saveChanges(): void {
    this.dialogRef.close({save: true, periodToSave: this.selectedPeriod});
  }

  applyTransactionFee(event: any) {
    this.selectedPeriod.applyTransactionFee = event.checked;
    if (this.selectedPeriod.bookingPricing) {
      this.selectedPeriod.bookingPricing.applyTransactionFees = event.checked;
    }
  }

  displayFeeType(type: string): string {
    if (type === 'RATE') return 'Turnover percentage';
    if (type === 'FIXED') return 'Fixed Rate';
    return '-';
  }

  get startDateMinValue(): any {
    return startDateMinValue(this.basePeriod, this.allPeriods, true);
  }

  get startDateMaxValue(): any {
    return startDateMaxValue(this.basePeriod, this.allPeriods);
  }

  get endDateMinValue(): any {
    return endDateMinValue(this.basePeriod, this.allPeriods);
  }

  // ensure that the end
  get endDateMaxValue(): any {
    const maxValue = endDateMaxValue(this.basePeriod, this.allPeriods);
    return !maxValue || maxValue > this.basePeriod.endDate ? this.basePeriod.endDate : maxValue;
  }

  onStartDateChange(newDate: Date) {
    this.selectedPeriod.startDate = newDate;
  }

  onEndDateChange(newDate: Date) {
    this.selectedPeriod.endDate = toIsoDateEndOfDay(newDate);
  }

  get filteredPatternTypes() {
    return this.patternTypes;
  }

  get displayPaymentMethodsTable(): boolean {
    return this.selectedPeriod.applyTransactionFee === true;
  }

  get editingValues() {
    return this.editType === 'values';
  }

  protected readonly PanelType = PanelType;
  protected readonly OccupancyType = OccupancyType;
  protected readonly Date = Date;
  protected readonly RentalPricingOption = RentalPricingOption;
  protected readonly SpacePricingRateCategory = SpacePricingRateCategory;
}
